/**
 * Running an API call from a page, with its loading and error state.
 *
 * Pages handle three states — loading, error, loaded — and hand-writing the
 * `useState` triple for each one is how the error case quietly gets left out.
 * This does it once.
 */
import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | undefined;
  /** True while a request is in flight, including a reload with data already shown. */
  loading: boolean;
  /** Runs the operation again. Stable — safe in a dependency list. */
  reload: () => void;
  /**
   * Replaces the loaded value without a request.
   *
   * For the case where a mutation already answered with the new state: renaming
   * a resource returns the resource, so there is nothing to go and fetch.
   */
  setData: (value: T) => void;
}

export interface UseAsyncOptions {
  /**
   * When false, nothing runs. A hook that starts disabled is not loading — an
   * empty search shows no spinner; one disabled later keeps the state it had.
   *
   * For a call that has no meaning yet — an id still being resolved, a request
   * that needs a permission the caller may not hold.
   */
  enabled?: boolean;
}

/**
 * Runs `operation` on mount and whenever `deps` change.
 *
 * `operation` is intentionally *not* a dependency: it is a new function on every
 * render, so depending on it would re-request forever. `deps` is the honest
 * statement of what the request is made of — the same bargain `useEffect` makes.
 */
export function useAsync<T>(
  operation: () => Promise<T>,
  deps: DependencyList,
  options: UseAsyncOptions = {},
): AsyncState<T> {
  const enabled = options.enabled ?? true;

  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(enabled);
  const [nonce, setNonce] = useState(0);

  // Every run takes a ticket; only the newest one is allowed to write. Without
  // this, a slow first request can land after a fast second and overwrite the
  // newer answer with the older one — the classic folder-navigation bug where
  // the list snaps back to the folder you just left.
  const ticket = useRef(0);
  const latest = useRef(operation);
  latest.current = operation;

  useEffect(() => {
    if (!enabled) return;

    const mine = ++ticket.current;
    setLoading(true);
    setError(undefined);

    latest.current().then(
      (value) => {
        if (ticket.current !== mine) return;
        setData(value);
        setLoading(false);
      },
      (cause: unknown) => {
        if (ticket.current !== mine) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
        setLoading(false);
      },
    );

    // Nothing to clean up: the ticket already makes a stale result a no-op, and
    // aborting the request would not make the page any more correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const replace = useCallback((value: T) => {
    // Takes a ticket too, so an in-flight request cannot overwrite the value a
    // mutation just gave us.
    ticket.current += 1;
    setData(value);
    setError(undefined);
    setLoading(false);
  }, []);

  return { data, error, loading, reload, setData: replace };
}
